import { useMemo } from 'react';
import useSWR from 'swr';
import type { PolymarketMarket } from '@/components/predic/detail/market/types';
import { useTrackingWallets } from '@/contexts/TrackingWalletsProvider';

export type HoldingPositionStatus = NonNullable<PolymarketMarket['uma_resolution_status']>;

export interface HoldingPositionItem {
  id: string;
  outcome: 'YES' | 'NO' | string;
  label: string;
  shares: number;
  currentValue: number;
  initialValue: number;
  cashPnl: number;
  status?: HoldingPositionStatus;
}
export interface HoldingPosition {
  proxyWallet?: string;
  asset?: string;
  conditionId?: string;
  marketSlug?: string;
  title?: string;
  outcome: string;
  size: number;
  avgPrice?: number;
  currentValue: number;
  initialValue?: number;
  cashPnl?: number;
  percentPnl?: number;
  realizedPnl?: number;
  icon?: string;
  image?: string;
  totalBought?: number;
  curPrice?: number;
  redeemable?: boolean;
  mergeable?: boolean;
  slug?: string;
  eventId?: string;
  eventSlug?: string;
  outcomeIndex?: number;
  endDate?: string;
}

export interface HoldingPositionsSummary {
  wallet: string;
  totalValue: number;
  totalPnl: number;
  totalPnlPercent: number;
  walletAddress: string;
  status?: HoldingPositionStatus;
  positions: HoldingPositionItem[];
  rawPositions: HoldingPosition[];
}

export interface UseHoldingPositionsOptions {
  /** 查询的钱包地址列表；缺省时用 useTrackingWallets 的 trackWallets */
  wallets?: string[];
  markets?: PolymarketMarket[];
  market?: string | string[];
  eventId?: string | number;
  limit?: number;
  sizeThreshold?: number;
  sortBy?: 'CURRENT' | 'TOKENS' | 'CASH' | string;
  sortDirection?: 'ASC' | 'DESC';
  redeemable?: boolean;
}

export interface PolymarkPosition {
  proxyWallet: string;
  asset: string;
  conditionId: string;
  size: number;
  avgPrice: number;
  initialValue: number;
  currentValue: number;
  cashPnl: number;
  percentPnl: number;
  totalBought: number;
  realizedPnl: number;
  percentRealizedPnl: number;
  curPrice: number;
  redeemable: boolean;
  mergeable: boolean;
  title: string;
  slug: string;
  icon: string;
  eventSlug: string;
  outcome: string;
  outcomeIndex: number;
  oppositeOutcome: string;
  oppositeAsset: string;
  endDate: string;
  negativeRisk: boolean;
}

interface WalletHoldingPositionsResult {
  wallet: string;
  positions: PolymarkPosition[];
}

const DEFAULT_LIMIT = 100;

async function fetchWalletHoldingPositions(
  wallet: string,
  options: UseHoldingPositionsOptions = {},
): Promise<PolymarkPosition[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('user', wallet);
  searchParams.set('limit', String(options.limit ?? DEFAULT_LIMIT));
  searchParams.set('sizeThreshold', String(options.sizeThreshold ?? 1));
  searchParams.set('sortBy', options.sortBy ?? 'TOKENS');
  searchParams.set('sortDirection', options.sortDirection ?? 'DESC');

  normalizeMarketOption(options.market).forEach((market) => {
    searchParams.append('market', market);
  });
  if (options.eventId !== undefined && options.eventId !== null) {
    searchParams.set('eventId', String(options.eventId));
  }
  if (options.redeemable !== undefined) {
    searchParams.set('redeemable', String(options.redeemable));
  }

  const response = await fetch(
    `https://data-api.polymarket.com/positions?${searchParams.toString()}`,
    {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch holding positions: ${response.status}`);
  }

  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

async function fetchHoldingPositions(
  wallets: string[],
  options: UseHoldingPositionsOptions = {},
): Promise<WalletHoldingPositionsResult[]> {
  const results = await Promise.allSettled(
    wallets.map(async (wallet) => ({
      wallet,
      positions: await fetchWalletHoldingPositions(wallet, options),
    })),
  );

  return results.flatMap((result) => (result.status === 'fulfilled' ? result.value : []));
}

export function useHoldingPositions(options: UseHoldingPositionsOptions = {}) {
  // 默认用手动追踪钱包；调用方传入 options.wallets 时优先
  const { wallets: contextWallets } = useTrackingWallets();
  const trackWallets = contextWallets?.trackWallets;
  const wallets = useMemo(
    () => [...new Set((options.wallets ?? trackWallets ?? []).filter(Boolean))],
    [options.wallets, trackWallets],
  );
  const walletKey = wallets.join(',');

  const swrKey = wallets.length
    ? [
        'holding-positions',
        walletKey,
        getMarketKey(options.market),
        options.eventId ?? '',
        options.limit ?? DEFAULT_LIMIT,
        options.sizeThreshold ?? 1,
        options.sortBy ?? 'TOKENS',
        options.sortDirection ?? 'DESC',
        options.redeemable ?? '',
      ]
    : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    swrKey,
    () => fetchHoldingPositions(wallets, options),
    {
      revalidateOnFocus: false,
    },
  );

  const conditionTitleById = useMemo(() => {
    const map = new Map<string, string>();

    options.markets?.forEach((market) => {
      if (market.condition_id && market.group_item_title) {
        map.set(market.condition_id, market.group_item_title);
      }
    });

    return map;
  }, [options.markets]);

  const conditionOrderById = useMemo(() => {
    const map = new Map<string, number>();

    options.markets?.forEach((market, index) => {
      if (market.condition_id && !map.has(market.condition_id)) {
        map.set(market.condition_id, index);
      }
    });

    return map;
  }, [options.markets]);

  const marketStatusById = useMemo(() => {
    const map = new Map<string, HoldingPositionStatus>();

    options.markets?.forEach((market) => {
      if (!market.uma_resolution_status) return;

      [
        market.condition_id,
        market.market_slug,
        market.market_id,
        market.clob_tokens_id_yes,
        market.clob_tokens_id_no,
      ].forEach((key) => {
        if (key) {
          map.set(key, market.uma_resolution_status!);
        }
      });
    });

    return map;
  }, [options.markets]);

  const walletSummaries = useMemo<HoldingPositionsSummary[]>(() => {
    if (!wallets.length || error || !data?.length) return [];

    return data
      .filter((walletData) => walletData.positions.length > 0)
      .map((walletData) =>
        createWalletSummary(
          walletData.wallet,
          walletData.positions,
          conditionTitleById,
          conditionOrderById,
          marketStatusById,
        ),
      )
      .sort(
        (a, b) => getSummaryOrder(a, conditionOrderById) - getSummaryOrder(b, conditionOrderById),
      );
  }, [conditionOrderById, conditionTitleById, data, wallets.length, error, marketStatusById]);

  return {
    walletSummaries,
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

function normalizeMarketOption(market?: string | string[]) {
  const markets = Array.isArray(market) ? market : market ? [market] : [];

  return [...new Set(markets.map((item) => item.trim()).filter(Boolean))];
}

function getMarketKey(market?: string | string[]) {
  return normalizeMarketOption(market).join('|');
}

function createWalletSummary(
  wallet: string,
  positions: HoldingPosition[],
  conditionTitleById: Map<string, string>,
  conditionOrderById: Map<string, number>,
  marketStatusById: Map<string, HoldingPositionStatus>,
): HoldingPositionsSummary {
  const sortedPositions = sortPositionsByCurrentValue(positions, conditionOrderById);
  const totalValue = positions.reduce((sum, position) => sum + toNumber(position.currentValue), 0);
  const totalPnl = positions.reduce((sum, position) => sum + toNumber(position.cashPnl), 0);
  const investedValue = positions.reduce(
    (sum, position) => sum + toNumber(position.initialValue),
    0,
  );
  const totalPnlPercent = investedValue ? (totalPnl / investedValue) * 100 : 0;

  const positionItems = sortedPositions.map((position) =>
    toHoldingPositionItem(
      position,
      conditionTitleById.get(getPositionConditionId(position)),
      getPositionStatus(position, marketStatusById),
    ),
  );

  return {
    wallet,
    totalValue,
    totalPnl,
    totalPnlPercent,
    walletAddress: shortenAddress(wallet),
    status: getSummaryStatus(positionItems),
    positions: positionItems,
    rawPositions: sortedPositions,
  };
}

function sortPositionsByCurrentValue(
  positions: HoldingPosition[],
  conditionOrderById: Map<string, number>,
) {
  return [...positions].sort((a, b) => {
    const valueDiff = toNumber(b.currentValue) - toNumber(a.currentValue);
    if (valueDiff !== 0) return valueDiff;

    return getPositionOrder(a, conditionOrderById) - getPositionOrder(b, conditionOrderById);
  });
}

function getSummaryOrder(
  summary: HoldingPositionsSummary,
  conditionOrderById: Map<string, number>,
) {
  return summary.rawPositions.reduce(
    (minOrder, position) => Math.min(minOrder, getPositionOrder(position, conditionOrderById)),
    Number.MAX_SAFE_INTEGER,
  );
}

function getPositionOrder(position: HoldingPosition, conditionOrderById: Map<string, number>) {
  return conditionOrderById.get(getPositionConditionId(position)) ?? Number.MAX_SAFE_INTEGER;
}

function getPositionConditionId(position: HoldingPosition) {
  return (
    position.conditionId || position.marketSlug || position.slug || position.asset || 'unknown'
  );
}

function getPositionStatus(
  position: HoldingPosition,
  marketStatusById: Map<string, HoldingPositionStatus>,
) {
  const candidateKeys = [
    position.conditionId,
    position.marketSlug,
    position.slug,
    position.asset,
  ].filter(Boolean) as string[];

  for (const key of candidateKeys) {
    const status = marketStatusById.get(key);
    if (status) return status;
  }

  return undefined;
}

function getSummaryStatus(positions: HoldingPositionItem[]) {
  if (positions.length === 0) return undefined;
  if (positions.every((position) => position.status === 'resolved')) return 'resolved';
  if (positions.some((position) => position.status === 'proposed')) return 'proposed';
  return positions.find((position) => position.status)?.status;
}

function toHoldingPositionItem(
  position: HoldingPosition,
  label?: string,
  status?: HoldingPositionStatus,
): HoldingPositionItem {
  return {
    id: position.asset || `${position.conditionId}-${position.outcome}`,
    outcome: position.outcome,
    label: label || position.outcome,
    shares: toNumber(position.size),
    currentValue: toNumber(position.currentValue),
    initialValue: toNumber(position.initialValue),
    cashPnl: toNumber(position.cashPnl),
    status,
  };
}

function shortenAddress(address: string) {
  if (address.length <= 12) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

function toNumber(value: number | string | null | undefined) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}
